import { CaretLeft as ChevronLeft, CaretRight as ChevronRight } from '../ui/icons';
import { useEffect, useRef, useState } from 'react';
import { useProducts } from '../../hooks/useProducts';
import { useI18n } from '../../hooks/useI18n';
import ProductCard from '../products/ProductCard';
import { ProductCardSkeleton } from '../ui/Skeleton';
import CatalogCta from './CatalogCta';

function getVisibleCount() {
  if (window.innerWidth >= 1024) return 3;
  if (window.innerWidth >= 640) return 2;
  return 1;
}

export default function FeaturedProducts() {
  const { featuredProducts, loading } = useProducts();
  const { t } = useI18n();
  const products = featuredProducts.slice(0, 5);
  const [visibleCount, setVisibleCount] = useState(() => getVisibleCount());
  const [slideIndex, setSlideIndex] = useState(visibleCount);
  const [paused, setPaused] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const swipeStartX = useRef<number | null>(null);

  useEffect(() => {
    const updateVisibleCount = () => setVisibleCount(getVisibleCount());
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  useEffect(() => {
    setSlideIndex(visibleCount);
    setIsResetting(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setIsResetting(false)));
  }, [visibleCount, products.length]);

  const maxVisible = Math.min(visibleCount, products.length);
  const loopedProducts = products.length > 1
    ? [...products.slice(-maxVisible), ...products, ...products.slice(0, maxVisible)]
    : products;
  const activeIndex = products.length ? (slideIndex - maxVisible + products.length) % products.length : 0;

  useEffect(() => {
    if (paused || products.length < 2) return undefined;
    const timer = window.setInterval(() => setSlideIndex((current) => current + 1), 5000);
    return () => window.clearInterval(timer);
  }, [paused, products.length]);

  if (loading) {
    return (
      <section data-testid="storefront-catalog" className="home-snap-section pt-12 pb-20 sm:pb-24">
        <div className="container mx-auto px-4">
          <div className="mb-8 flex items-center justify-between gap-3">
            <div className="h-8 w-48 bg-line/30 rounded animate-pulse" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[...Array(4)].map((_, i) => <ProductCardSkeleton key={i} />)}
          </div>
        </div>
      </section>
    );
  }

  if (products.length === 0) return null;

  const move = (direction: -1 | 1) => {
    if (products.length > 1) setSlideIndex((current) => current + direction);
  };

  const handlePointerUp = (clientX: number) => {
    if (swipeStartX.current === null) return;
    const distance = clientX - swipeStartX.current;
    swipeStartX.current = null;
    if (Math.abs(distance) >= 40) move(distance < 0 ? 1 : -1);
  };

  const normalizeLoop = () => {
    if (products.length < 2) return;
    const firstCloneIndex = products.length + maxVisible;
    const normalizedIndex = slideIndex >= firstCloneIndex ? maxVisible : slideIndex <= 0 ? products.length : null;
    if (normalizedIndex === null) return;
    setIsResetting(true);
    setSlideIndex(normalizedIndex);
    requestAnimationFrame(() => requestAnimationFrame(() => setIsResetting(false)));
  };

  return (
    <section className="home-snap-section pt-12 pb-20 sm:pb-24" aria-roledescription="carousel" aria-label={t('home_featured')}>
      <div className="container mx-auto px-4">
        <div className="mb-8 flex items-center justify-between gap-3">
          <h2 className="font-display min-w-0 text-2xl font-bold md:text-4xl"><span className="bg-gradient-to-r from-brand via-accent to-brand bg-clip-text text-transparent">{t('home_featured')}</span></h2>
          <CatalogCta label={t('btn_view_all')} className="shrink-0" />
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          {products.length > 1 && <button onClick={() => move(-1)} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line bg-surface text-ink shadow-lg shadow-canvas/30 transition-colors hover:border-brand hover:text-brand" aria-label="Previous featured product"><ChevronLeft className="h-5 w-5" /></button>}
          <div className="min-w-0 flex-1 self-stretch overflow-hidden pt-2 touch-pan-y" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)} onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false); }} onPointerDown={(event) => { swipeStartX.current = event.clientX; }} onPointerUp={(event) => handlePointerUp(event.clientX)} onPointerCancel={() => { swipeStartX.current = null; }}>
            <div className={`flex h-full ${isResetting ? '' : 'transition-transform duration-500 ease-out'}`} style={{ transform: `translateX(-${slideIndex * (100 / maxVisible)}%)` }} onTransitionEnd={normalizeLoop}>
              {loopedProducts.map((product, index) => <div key={`${product.id}-${index}`} className="h-full shrink-0 px-2" style={{ width: `${100 / maxVisible}%` }}><ProductCard product={product} /></div>)}
            </div>
          </div>
          {products.length > 1 && <button onClick={() => move(1)} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line bg-surface text-ink shadow-lg shadow-canvas/30 transition-colors hover:border-brand hover:text-brand" aria-label="Next featured product"><ChevronRight className="h-5 w-5" /></button>}
        </div>

        {products.length > 1 && <div className="mt-5 flex justify-center gap-2" aria-label="Featured product slides">{products.map((product, index) => <button key={product.id} onClick={() => setSlideIndex(maxVisible + index)} className={`h-2 rounded-full transition-all ${index === activeIndex ? 'w-6 bg-brand' : 'w-2 bg-line hover:bg-muted'}`} aria-label={`Show featured product ${index + 1}`} aria-current={index === activeIndex ? 'true' : undefined} />)}</div>}
      </div>
    </section>
  );
}
